import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, LinearProgress, Card, CardContent, Grid,
  Stack, Tooltip, CircularProgress, Alert, Snackbar,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  HourglassEmpty as PendingIcon,
  History as HistoryIcon,
  CalendarToday as CalendarIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Today as TodayIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import api from '../api';

// Порядок сортировки ролей
const ROLE_ORDER = { sewer: 0, frame_worker: 1, upholsterer: 2 };

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatPeriod = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const SalaryManagement = () => {
  const [weeklyData, setWeeklyData] = useState(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [workerHistory, setWorkerHistory] = useState(null);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [creatingWorkerId, setCreatingWorkerId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const salaryRequestRef = useRef({ sequence: 0, controller: null });

  const loadSalaryData = useCallback(async (offset) => {
    salaryRequestRef.current.controller?.abort();
    const controller = new AbortController();
    const sequence = salaryRequestRef.current.sequence + 1;
    salaryRequestRef.current = { sequence, controller };
    setLoading(true);
    setErrorMessage('');
    try {
      const weekRes = await api.get('/salary/week', {
        params: { week_offset: offset },
        signal: controller.signal,
      });
      if (salaryRequestRef.current.sequence !== sequence) return;
      const sorted = [...(weekRes.data.workers || [])].sort((a, b) => {
        const orderA = ROLE_ORDER[a.role] ?? 99;
        const orderB = ROLE_ORDER[b.role] ?? 99;
        return orderA - orderB;
      });
      setWeeklyData({ ...weekRes.data, workers: sorted });
    } catch (error) {
      if (controller.signal.aborted || salaryRequestRef.current.sequence !== sequence) return;
      console.error('Ошибка загрузки данных о зарплате:', error);
      setWeeklyData(null);
      setErrorMessage(error.response?.data?.detail || 'Не удалось загрузить расчёт зарплаты');
    } finally {
      if (salaryRequestRef.current.sequence === sequence && !controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSalaryData(weekOffset);
    return () => {
      salaryRequestRef.current.controller?.abort();
      salaryRequestRef.current.sequence += 1;
    };
  }, [weekOffset, loadSalaryData]);

  const openHistory = async (workerId) => {
    try {
      const response = await api.get(`/salary/history/${workerId}`);
      setWorkerHistory(response.data);
      setOpenHistoryDialog(true);
    } catch (error) {
      console.error('Ошибка загрузки истории:', error);
      setErrorMessage(error.response?.data?.detail || 'Ошибка загрузки истории');
    }
  };

  const createSalaryRecord = async (workerId, periodStart, periodEnd) => {
    if (!window.confirm(`Создать ведомость за период ${formatPeriod(periodStart)} — ${formatPeriod(periodEnd)}?`)) return;
    try {
      setCreatingWorkerId(workerId);
      const response = await api.post('/salary/create-record', {
        worker_id: workerId,
        period_start: periodStart,
        period_end: periodEnd,
      });
      setSuccessMessage(`Ведомость №${String(response.data.id).padStart(6, '0')} создана`);
      await loadSalaryData(weekOffset);
    } catch (error) {
      console.error('Ошибка создания ведомости:', error);
      setErrorMessage(error.response?.data?.detail || 'Ошибка при создании ведомости');
    } finally {
      setCreatingWorkerId(null);
    }
  };

  const downloadSalaryPdf = async (workerId, workerName, periodStart, periodEnd, recordId) => {
    try {
      const params = { period_start: periodStart, period_end: periodEnd };
      if (recordId) params.record_id = recordId;
      const response = await api.get(`/salary/pdf/${workerId}`, {
        params,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (workerName || 'worker').replace(/[^\p{L}\p{N}_-]+/gu, '_');
      link.download = `vedomost_${safeName}_${periodStart}_${periodEnd}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Ошибка скачивания PDF:', error);
      let detail = error.response?.data?.detail;
      if (error.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await error.response.data.text());
          detail = body.detail;
        } catch (_) {}
      }
      setErrorMessage(detail || 'Ошибка при скачивании PDF');
    }
  };

  const togglePaidStatus = async (recordId, currentlyPaid) => {
    const prompt = currentlyPaid
      ? 'Снять отметку о выплате? Это изменит статус ведомости.'
      : 'Подтвердить выплату по этой ведомости?';
    if (!window.confirm(prompt)) return;
    try {
      setActionLoadingId(recordId);
      if (currentlyPaid) {
        await api.post('/salary/unpay', { salary_record_id: recordId });
      } else {
        await api.post('/salary/pay', { salary_record_id: recordId });
      }
      // Reload history if open
      if (workerHistory) {
        const response = await api.get(`/salary/history/${workerHistory.worker_id}`);
        setWorkerHistory(response.data);
      }
      loadSalaryData(weekOffset);
    } catch (error) {
      console.error('Ошибка изменения статуса:', error);
      setErrorMessage(error.response?.data?.detail || 'Ошибка изменения статуса');
    } finally {
      setActionLoadingId(null);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      'sewer': 'Швея',
      'frame_worker': 'Каркасник',
      'upholsterer': 'Обстрельщик',
    };
    return labels[role] || role;
  };

  const getRoleColor = (role) => {
    if (role === 'sewer') return 'secondary';
    if (role === 'frame_worker') return 'primary';
    return 'success';
  };

  const weekLabel = (() => {
    if (weekOffset === 0) return 'Текущая неделя';
    if (weekOffset === -1) return 'Прошлая неделя';
    if (weekOffset === 1) return 'Следующая неделя';
    if (weekOffset < 0) return `${Math.abs(weekOffset)} нед. назад`;
    return `+${weekOffset} нед.`;
  })();

  if (loading && !weeklyData) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '1.55rem', sm: '2.125rem' } }}>
        <MoneyIcon sx={{ mr: 1, fontSize: 40 }} />
        Зарплата по неделям
      </Typography>

      {/* Навигация по неделям */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Предыдущая неделя">
              <IconButton onClick={() => setWeekOffset((o) => o - 1)} color="primary">
                <PrevIcon />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant={weekOffset === 0 ? 'contained' : 'outlined'}
              startIcon={<TodayIcon />}
              onClick={() => setWeekOffset(0)}
            >
              Текущая неделя
            </Button>
            <Tooltip title="Следующая неделя">
              <span>
                <IconButton onClick={() => setWeekOffset((o) => o + 1)} color="primary" disabled={weekOffset >= 0}>
                  <NextIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <Box>
            <Chip
              icon={<CalendarIcon />}
              color={weekOffset === 0 ? 'primary' : 'default'}
              label={`${weekLabel} · ${weeklyData?.period?.start || ''} — ${weeklyData?.period?.end || ''}`}
              sx={{ fontWeight: 600, maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
            />
          </Box>
        </Stack>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {/* Мобильные карточки */}
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.25, mb: 2 }}>
        {(!weeklyData?.workers || weeklyData.workers.length === 0) ? (
          <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 3, border: '1px dashed', borderColor: 'divider', boxShadow: 'none' }}>
            <Typography color="text.secondary" variant="body2">
              За эту неделю нет выполненных задач
            </Typography>
          </Paper>
        ) : (
          weeklyData.workers.map((worker) => (
            <Paper
              key={worker.worker_id}
              elevation={0}
              sx={{
                p: 1.75,
                borderRadius: 2.5,
                backgroundColor: '#fff',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:active': { transform: 'scale(0.995)', boxShadow: '0 4px 14px rgba(15,23,42,0.08)' },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, mb: 1.25 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>
                    {worker.worker_name}
                  </Typography>
                  <Chip
                    label={getRoleLabel(worker.role)}
                    size="small"
                    color={getRoleColor(worker.role)}
                    sx={{ mt: 0.75, height: 22, fontSize: '0.7rem' }}
                  />
                </Box>
                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1, color: '#0f172a', letterSpacing: '-0.01em' }}>
                    {worker.total_payment.toLocaleString('ru-RU')}
                    <Typography component="span" sx={{ ml: 0.5, color: 'text.secondary', fontWeight: 600, fontSize: '0.85rem' }}>₸</Typography>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {worker.total_tasks} задач
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, pt: 1.25, borderTop: '1px solid', borderColor: '#f1f5f9' }}>
                <Box>
                  <Tooltip title="История + детализация">
                    <IconButton size="small" onClick={() => openHistory(worker.worker_id)} sx={{ color: 'text.secondary' }}>
                      <HistoryIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={worker.record ? 'Скачать зафиксированную PDF-ведомость' : 'Сначала создайте ведомость'}>
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => downloadSalaryPdf(worker.worker_id, worker.worker_name, weeklyData.period.start, weeklyData.period.end, worker.record?.id)}
                        disabled={!worker.record}
                      >
                        <PdfIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={creatingWorkerId === worker.worker_id ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                  onClick={() => createSalaryRecord(worker.worker_id, weeklyData.period.start, weeklyData.period.end)}
                  disabled={worker.total_payment <= 0 || Boolean(worker.record) || creatingWorkerId !== null}
                  sx={{ py: 0.5, px: 1.5, fontSize: '0.75rem' }}
                >
                  {worker.record ? (worker.record.is_paid ? 'Выплачено' : `Ведомость №${worker.record.id}`) : 'Создать ведомость'}
                </Button>
              </Box>
            </Paper>
          ))
        )}
      </Box>

      {/* Таблица работников — десктоп */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, display: { xs: 'none', md: 'block' } }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>Работник</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Роль</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Выполнено задач</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Сумма к выплате</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!weeklyData?.workers || weeklyData.workers.length === 0) && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  За эту неделю нет выполненных задач
                </TableCell>
              </TableRow>
            )}
            {weeklyData?.workers.map((worker, idx) => {
              const prevRole = idx > 0 ? weeklyData.workers[idx - 1].role : null;
              const showDivider = prevRole && prevRole !== worker.role;
              return (
                <React.Fragment key={worker.worker_id}>
                  {showDivider && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 0.5, bgcolor: '#f8fafc' }} />
                    </TableRow>
                  )}
                  <TableRow hover>
                    <TableCell>{worker.worker_name}</TableCell>
                    <TableCell>
                      <Chip label={getRoleLabel(worker.role)} size="small" color={getRoleColor(worker.role)} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={worker.total_tasks} color="default" size="small" />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                        {worker.total_payment.toLocaleString()} ₸
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="История + детализация по диванам">
                        <IconButton color="primary" onClick={() => openHistory(worker.worker_id)}>
                          <HistoryIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={worker.record ? 'Скачать зафиксированную PDF-ведомость' : 'Сначала создайте ведомость'}>
                        <span>
                          <IconButton
                            color="error"
                            onClick={() => downloadSalaryPdf(worker.worker_id, worker.worker_name, weeklyData.period.start, weeklyData.period.end, worker.record?.id)}
                            disabled={!worker.record}
                          >
                            <PdfIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Button
                        size="small" variant="contained" color="success"
                        startIcon={creatingWorkerId === worker.worker_id ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                        onClick={() => createSalaryRecord(worker.worker_id, weeklyData.period.start, weeklyData.period.end)}
                        sx={{ ml: 1 }}
                        disabled={worker.total_payment <= 0 || Boolean(worker.record) || creatingWorkerId !== null}
                      >
                        {worker.record ? (worker.record.is_paid ? 'Выплачено' : `Ведомость №${worker.record.id}`) : 'Создать ведомость'}
                      </Button>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Итого за неделю */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 2, py: 1.5, bgcolor: '#f8fafc', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <CalendarIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
          {weeklyData?.period.start} — {weeklyData?.period.end} · Работников: {weeklyData?.workers.length || 0}
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Итого: {weeklyData ? `${weeklyData.total_payment.toLocaleString()} ₸` : '0 ₸'}
        </Typography>
      </Box>

      {/* History Dialog */}
      <Dialog open={openHistoryDialog} onClose={() => setOpenHistoryDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 1 }} />
            История выплат — {workerHistory?.worker_name}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {workerHistory && (
            <>
              {/* Сводка */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Выплачено всего</Typography>
                      <Typography variant="h5" color="success.main">
                        {workerHistory.summary.total_paid.toLocaleString()} ₸
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>К выплате</Typography>
                      <Typography variant="h5" color="warning.main">
                        {workerHistory.summary.total_unpaid.toLocaleString()} ₸
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Ведомостей</Typography>
                      <Typography variant="h5">{workerHistory.summary.total_records}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* История ведомостей */}
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <HistoryIcon sx={{ mr: 1, fontSize: 22 }} />
                Ведомости
              </Typography>
              {workerHistory.history.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  Ведомости ещё не созданы. Нажмите «Создать ведомость» в таблице выше, чтобы зафиксировать выплату за период.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell><strong>Период</strong></TableCell>
                        <TableCell align="right"><strong>Сумма</strong></TableCell>
                        <TableCell align="center"><strong>Статус</strong></TableCell>
                        <TableCell><strong>Дата выплаты</strong></TableCell>
                        <TableCell align="center"><strong>Действие</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {workerHistory.history.map((record) => (
                        <TableRow key={record.id} hover>
                          <TableCell>{formatPeriod(record.period_start)} — {formatPeriod(record.period_end)}</TableCell>
                          <TableCell align="right">
                            <strong>{record.amount.toLocaleString()} ₸</strong>
                          </TableCell>
                          <TableCell align="center">
                            {record.is_paid ? (
                              <Chip label="Выплачено" color="success" size="small" icon={<CheckIcon />} />
                            ) : (
                              <Chip label="Не оплачено" color="warning" size="small" icon={<PendingIcon />} />
                            )}
                          </TableCell>
                          <TableCell>{record.paid_at ? formatDate(record.paid_at) : '-'}</TableCell>
                          <TableCell align="center">
                            <Tooltip title="Скачать PDF">
                              <IconButton
                                size="small"
                                color="error"
                                sx={{ mr: 0.5 }}
                                onClick={() => downloadSalaryPdf(workerHistory.worker_id, workerHistory.worker_name, record.period_start, record.period_end, record.id)}
                              >
                                <PdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {record.is_paid ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                disabled={actionLoadingId === record.id}
                                onClick={() => togglePaidStatus(record.id, true)}
                              >
                                Отменить выплату
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<CheckIcon />}
                                disabled={actionLoadingId === record.id}
                                onClick={() => togglePaidStatus(record.id, false)}
                              >
                                Отметить выплаченным
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHistoryDialog(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={Boolean(errorMessage)} autoHideDuration={6000} onClose={() => setErrorMessage('')}>
        <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ width: '100%' }}>{errorMessage}</Alert>
      </Snackbar>
      <Snackbar open={Boolean(successMessage)} autoHideDuration={4000} onClose={() => setSuccessMessage('')}>
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ width: '100%' }}>{successMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SalaryManagement;
